import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LabelSwitch from '../../../components/LabelSwitch/LabelSwitch';
import { SellerRoutesEnum } from '../../../core/enums';
import { formatNumber } from '../../../core/helpers';
import PageLayout from '../../../wrappers/PageLayout';
import FeesItem from './FeesItem';
import MonthCollapse, { MonthItem } from './MonthCollapse';
import './SellerAmazonFees.scss';

export type CostType = 'best' | 'worse';

export default function SellerAmazonFees() {
  const [activeCollapses, setActiveCollapses] = useState([
    'month-' + new Date().getMonth(),
  ]);

  const { t } = useTranslation();

  const [selectedType, setSelectedType] = useState<CostType>('best');

  const onCollpaseBtnClick = (id: string) => () => {
    setActiveCollapses((p) => {
      if (p.includes(id)) {
        return p.filter((s) => s !== id);
      }
      return [...p, id];
    });
  };
  return (
    <PageLayout
      className='flex-grow-1 PageLayout--SellerAmazonFees'
      rightColumn={
        <div>
          <div className='Paper pb-4 pt-2 px-3'>
            <div className='fw-600'>{t('In 2022 paid fees')}</div>
            <div className='Text--30 fw-900 mb-3'>
              {formatNumber(17140, {
                currency: 'eur',
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
              })}
            </div>
            <div className='fw-600 mb-2'>{t('You can optimize fees:')}</div>
            <ol className='ps-3 SellerAmazonFees__list'>
              <li className='mb-3'>
                <div className='fw-600'>{t('Turn on warehouse in France')}</div>
                <div>
                  {t('Estimated savings *n*€/month').replace('*n*', '103')}
                </div>
              </li>
              <li>
                <div className='fw-600'>{t('Optimize inventory')}</div>
                <div className='mb-1'>
                  {t(
                    'You have *n* products with costs higher than selling price'
                  ).replace('*n*', '5')}
                </div>
                <div className='mb-1 pb-2'>
                  {t('Estimated savings *n*€/month').replace('*n*', '90')}
                </div>
                <div className='mb-2'>
                  {t(
                    'You have *n* products with low fees but small number in inventory'
                  ).replace('*n*', '3')}
                </div>
                <div>
                  {t('Estimated savings *n*€/month').replace('*n*', '90')}
                </div>
              </li>
            </ol>
          </div>
        </div>
      }
    >
      <div className='Paper px-4 pt-3 pb-4'>
        <div className='Text--30 fw-900 mb-4'>{t('Amazon fees')}</div>
        <div className='SellerAmazonFees__separator mx-n4' />
        <div className='pt-3 pb-4 d-flex align-items-center justify-content-between fw-800 Text--20'>
          <div>{t('Amazon selling plan')}</div>
          <div>
            {formatNumber(32)}€/{t('month')}
          </div>
        </div>
        <div className='SellerAmazonFees__separator mx-n4' />
        <div className='pt-3 pb-3 mb-3'>
          <div className='Text--20 fw-800 mb-3'>{t('Highlights')}</div>
          <div className='row SellerAmazonFees__row row-cols-auto'>
            {[
              {
                value: formatNumber(1904, {
                  currency: 'eur',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                }),
                label: 'per month paid fees on avarage',
              },
              {
                value: '+12%',
                label: 'fees increased in 2022',
              },
              {
                value: '-1.6%',
                label: 'Increased storage rates in 2022',
              },
            ].map(({ label, value }, i) => (
              <div className='col pt-2 SellerAmazonFees__col' key={i}>
                <div className='SellerAmazonFees__box px-2'>
                  <div
                    className='SellerAmazonFees__label fw-900 Text--30'
                    style={{
                      marginBottom: 2,
                    }}
                  >
                    {value}
                  </div>
                  <div className='multiline Text--gray'>{t(label)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className='SellerAmazonFees__separator mx-n4' />
        <div className='pt-3 d-flex align-items-center justify-content-between mb-3'>
          <div className='fw-800'>{t('Selling costs to sales ratio')}</div>
          <LabelSwitch<CostType>
            activeItem={selectedType}
            onClick={(v) => () => {
              setSelectedType(v);
            }}
            items={[
              { value: 'best', label: t('Best') },
              { value: 'worse', label: t('Other') },
            ]}
          />
        </div>
        <div className='pb-3 mb-3'>
          {[
            {
              name: 'Product name 1',
              amount: 9399,
              value: 1200,
            },
            {
              name: 'Product name 2',
              amount: 5900,
              value: 933,
            },
            {
              name: 'Product name 3',
              amount: 12000,
              value: 1099,
            },
            {
              name: 'Product name 4',
              amount: 10000,
              value: 999,
            },
            {
              name: 'Product name 5',
              amount: 1200,
              value: 119,
            },
          ].map((p) => (
            <FeesItem item={p} key={p.name} className='mb-2' />
          ))}
          <Link
            className='text-decoration-none Link--blue fw-600 mt-2'
            to={SellerRoutesEnum.Inventory + '?type=fees'}
          >
            {t('Show fee per product')}
          </Link>
        </div>
        <div className='SellerAmazonFees__separator mx-n4' />
        <div className='pt-3 pb-4 mb-3'>
          {[
            {
              name: t('DE'),
              amount: 2299,
              value: 500,
            },
            {
              name: t('ES'),
              amount: 1900,
              value: 2100,
            },
            {
              name: t('PL'),
              amount: 1500,
              value: 399,
            },
            {
              name: t('GB'),
              amount: 3300,
              value: 599,
            },
          ].map((p) => (
            <FeesItem item={p} key={p.name} className='mb-2' />
          ))}
        </div>
        <div className='SellerAmazonFees__separator mx-n4' />
        <div className='Text--20 fw-800 my-3'>{t('Fees by month')}</div>
        {DATA.map((item) => (
          <MonthCollapse
            key={item.date}
            data={item}
            className='mb-3'
            isOpened={activeCollapses.includes(
              `month-${new Date(item.date).getMonth()}`
            )}
            onCollpaseBtnClick={onCollpaseBtnClick(
              `month-${new Date(item.date).getMonth()}`
            )}
          />
        ))}
      </div>
    </PageLayout>
  );
}

const date = new Date();
date.setDate(1);
const month = date.getMonth();

const DATA = Array(month + 1)
  .fill({
    'Storage costs': {
      monthly: 72000,
      average: 8000,
      total: 80000,
    },
    'Fulfillment cost': {
      fba: 30000,
      service_addons: 28000,
      variable_closing_fee: 2000,
      total: 60000,
    },
    'Amazon fees': {
      referal: 29000,
      fixed: 1000,
      variable_closing: 10000,
      total: 40000,
    },
    Other: {
      goods_sold: 19000,
      miscellaneous_cost: 2000,
      total: 21000,
    },
  })
  .map((item: MonthItem, i) => {
    date.setMonth(month - i);

    const total = Object.values(item).reduce((acc: number, s) => {
      return (acc += s.total);
    }, 0);

    return { ...item, date: date.toISOString(), total };
  });
